/**
 * PIM Session Activation page.
 * Allows technicians/admins to activate a privileged access session for a customer.
 * Route: /customers/:customerId/activate
 */

import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ShieldCheck,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { CountdownTimer } from "@/components/sc/countdown-timer";
import { Button } from "@/components/ui/button";
import { useCustomerDetail } from "@/hooks/use-customers";
import {
  useActivateSession,
  useDeactivateSession,
  useExtendSession,
  usePimSessionsMine,
} from "@/hooks/use-pim-sessions";
import {
  TECHNICIAN_TIERS,
  TIER_ROLES,
  TIER_NUMERIC_RANK,
  ADMIN_PORTAL_LINKS,
  MSP_REALM,
  ENTRA_ROLE_DISPLAY_NAMES,
  isTeamsEnabled,
  type TechnicianTier,
  type PIMSession,
} from "@/types";
import { useTechnicianMe } from "@/hooks/use-technicians";

const DURATION_OPTIONS = [1, 2, 4, 6, 8] as const;
const MIN_JUSTIFICATION_LENGTH = 10;
const MAX_JUSTIFICATION_LENGTH = 500;

export function ActivateSessionPage() {
  const { customerId } = useParams({ strict: false }) as {
    customerId: string;
  };
  const navigate = useNavigate();

  const customerQuery = useCustomerDetail(customerId);
  const techMeQuery = useTechnicianMe();
  const activateSession = useActivateSession();
  const deactivateSession = useDeactivateSession();
  const extendSession = useExtendSession();

  // Check for existing active session for this customer
  const mySessionsQuery = usePimSessionsMine({ status: "Active" });
  const existingSession = (mySessionsQuery.data?.items ?? []).find(
    (s) => s.customerTenantId === customerId && s.status === "Active",
  );

  // Form state
  const [justification, setJustification] = useState("");
  const [durationHours, setDurationHours] = useState(1);
  const [activationTier, setActivationTier] = useState<
    TechnicianTier | "TeamsSpecialist" | ""
  >("");
  const [haloTicketId, setHaloTicketId] = useState("");
  const [validationError, setValidationError] = useState("");

  // Result state
  const [activatedSession, setActivatedSession] = useState<PIMSession | null>(
    null,
  );
  const [pendingApproval, setPendingApproval] = useState(false);

  // Extension form
  const [showExtend, setShowExtend] = useState(false);
  const [extendHours, setExtendHours] = useState(1);
  const [extendJustification, setExtendJustification] = useState("");

  const customer = customerQuery.data;
  const maxDuration = customer?.sessionDuration ?? 8;

  const validate = useCallback((): boolean => {
    if (justification.trim().length < MIN_JUSTIFICATION_LENGTH) {
      setValidationError(
        `Justification must be at least ${MIN_JUSTIFICATION_LENGTH} characters`,
      );
      return false;
    }
    if (justification.length > MAX_JUSTIFICATION_LENGTH) {
      setValidationError(
        `Justification must not exceed ${MAX_JUSTIFICATION_LENGTH} characters`,
      );
      return false;
    }
    if (durationHours < 1 || durationHours > maxDuration) {
      setValidationError(`Duration must be between 1 and ${maxDuration} hours`);
      return false;
    }
    setValidationError("");
    return true;
  }, [justification, durationHours, maxDuration]);

  function handleActivate() {
    if (!validate()) return;

    activateSession.mutate(
      {
        customerTenantId: customerId,
        justification: justification.trim(),
        durationHours,
        ...(activationTier ? { activationTier } : {}),
        ...(haloTicketId ? { haloTicketId: parseInt(haloTicketId, 10) } : {}),
      },
      {
        onSuccess: (data: unknown) => {
          const result = data as {
            session?: PIMSession;
            pendingApproval?: boolean;
            existing?: boolean;
          };
          if (result.pendingApproval) {
            setPendingApproval(true);
            toast.success("Session submitted for approval");
          } else if (result.session) {
            setActivatedSession(result.session);
            toast.success(
              result.existing
                ? "Reconnected to existing session"
                : "PIM session activated",
            );
          }
        },
        onError: (error: Error) => {
          toast.error(error.message || "Failed to activate session");
        },
      },
    );
  }

  function handleDeactivate(sessionId: string) {
    if (!confirm("Deactivate this session? Your privileged access will be removed immediately.")) return;
    deactivateSession.mutate(sessionId, {
      onSuccess: () => {
        toast.success("Session deactivated");
        setActivatedSession(null);
        void navigate({
          to: "/customers/$customerId",
          params: { customerId },
        });
      },
      onError: () => toast.error("Failed to deactivate session"),
    });
  }

  function handleExtend(sessionId: string) {
    if (extendJustification.trim().length < MIN_JUSTIFICATION_LENGTH) {
      toast.error("Extension justification must be at least 10 characters");
      return;
    }
    extendSession.mutate(
      {
        sessionId,
        request: {
          additionalHours: extendHours,
          justification: extendJustification.trim(),
        },
      },
      {
        onSuccess: (session) => {
          setActivatedSession(session);
          setShowExtend(false);
          setExtendJustification("");
          toast.success(`Session extended by ${extendHours}h`);
        },
        onError: () => toast.error("Failed to extend session"),
      },
    );
  }

  // Loading state
  if (customerQuery.isLoading) {
    return (
      <GlassCard className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
          <p className="text-body-sm text-sc-text-muted">
            Loading customer...
          </p>
        </div>
      </GlassCard>
    );
  }

  // Customer not found
  if (customerQuery.isError || !customer) {
    return (
      <GlassCard className="text-center py-16">
        <AlertTriangle size={48} className="mx-auto mb-4 text-sc-danger" />
        <h2 className="text-h3 font-display text-sc-navy mb-2">
          Customer not found
        </h2>
        <p className="text-body-sm text-sc-text-muted mb-4">
          The customer tenant may not be configured or synced from HaloPSA.
        </p>
        <button
          onClick={() => void navigate({ to: "/customers" })}
          className="text-body-sm text-sc-primary hover:underline"
        >
          Back to Customers
        </button>
      </GlassCard>
    );
  }

  // If there's an existing active session or we just activated one, show session view
  const activeSession = activatedSession ?? existingSession;
  if (activeSession) {
    return (
      <ActiveSessionView
        session={activeSession}
        customer={customer}
        customerId={customerId}
        onDeactivate={handleDeactivate}
        onExtend={handleExtend}
        showExtend={showExtend}
        setShowExtend={setShowExtend}
        extendHours={extendHours}
        setExtendHours={setExtendHours}
        extendJustification={extendJustification}
        setExtendJustification={setExtendJustification}
        extendPending={extendSession.isPending}
        deactivatePending={deactivateSession.isPending}
        navigate={navigate}
      />
    );
  }

  // Pending approval state
  if (pendingApproval) {
    return (
      <div className="space-y-6">
        <BackButton customerId={customerId} navigate={navigate} />
        <GlassCard className="text-center py-12">
          <Clock size={48} className="mx-auto mb-4 text-blue-400" />
          <h2 className="text-h2 font-display text-sc-navy mb-2">
            Awaiting Approval
          </h2>
          <p className="text-body text-sc-text-muted mb-4">
            Your PIM session request for{" "}
            <span className="font-medium text-sc-text">
              {customer.customerName}
            </span>{" "}
            has been submitted and is awaiting approval from an admin.
          </p>
          <p className="text-body-sm text-sc-text-muted">
            You will be notified when the request is approved or denied.
          </p>
          <button
            onClick={() =>
              void navigate({ to: "/pim-sessions" })
            }
            className="mt-6 text-body-sm text-sc-primary hover:underline"
          >
            View My Sessions
          </button>
        </GlassCard>
      </div>
    );
  }

  // Activation form
  return (
    <div className="space-y-6">
      <BackButton customerId={customerId} navigate={navigate} />

      {/* Header */}
      <div>
        <h1 className="text-h1 font-display text-sc-navy">
          Activate PIM Session
        </h1>
        <p className="text-body-sm text-sc-text-muted mt-1">
          Request privileged access to{" "}
          <span className="font-medium text-sc-text">
            {customer.customerName}
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activation form */}
        <div className="lg:col-span-2">
          <GlassCard>
            <h3 className="text-h3 font-display text-sc-navy mb-4">
              Session Details
            </h3>

            {/* Justification */}
            <div className="mb-4">
              <label
                htmlFor="justification"
                className="block text-body-sm font-medium text-sc-text mb-1"
              >
                Justification <span className="text-sc-danger">*</span>
              </label>
              <textarea
                id="justification"
                value={justification}
                onChange={(e) => {
                  setJustification(e.target.value);
                  if (validationError) setValidationError("");
                }}
                placeholder="Describe why you need elevated access (e.g., HaloPSA ticket number, task description)..."
                rows={3}
                maxLength={MAX_JUSTIFICATION_LENGTH}
                className="w-full rounded-input border border-sc-border bg-sc-input-bg px-3 py-2 text-body-sm text-sc-text placeholder:text-sc-text-muted resize-none focus:outline-none focus:ring-2 focus:ring-sc-primary/50"
              />
              <div className="flex justify-between mt-1">
                <span className="text-label text-sc-text-muted">
                  Min {MIN_JUSTIFICATION_LENGTH} characters
                </span>
                <span
                  className={`text-label ${
                    justification.length > MAX_JUSTIFICATION_LENGTH
                      ? "text-sc-danger"
                      : "text-sc-text-muted"
                  }`}
                >
                  {justification.length}/{MAX_JUSTIFICATION_LENGTH}
                </span>
              </div>
            </div>

            {/* Duration */}
            <div className="mb-4">
              <label className="block text-body-sm font-medium text-sc-text mb-1">
                Duration
              </label>
              <div className="flex gap-2">
                {DURATION_OPTIONS.filter((d) => d <= maxDuration).map(
                  (hours) => (
                    <button
                      key={hours}
                      onClick={() => setDurationHours(hours)}
                      className={`px-4 py-2 rounded-btn text-body-sm font-medium transition-colors ${
                        durationHours === hours
                          ? "bg-sc-primary text-white"
                          : "text-sc-text-muted border border-sc-border hover:bg-sc-surface-hover"
                      }`}
                    >
                      {hours}h
                    </button>
                  ),
                )}
              </div>
              <p className="text-label text-sc-text-muted mt-1">
                Max {maxDuration}h for {customer.customerName}
              </p>
            </div>

            {/* Activation Tier (optional) */}
            <div className="mb-6">
              <label
                htmlFor="activationTier"
                className="block text-body-sm font-medium text-sc-text mb-1"
              >
                Activation Tier{" "}
                <span className="text-sc-text-muted font-normal">
                  (optional)
                </span>
              </label>
              <select
                id="activationTier"
                value={activationTier}
                onChange={(e) =>
                  setActivationTier(e.target.value as TechnicianTier | "TeamsSpecialist" | "")
                }
                className="h-9 w-full max-w-xs rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
              >
                {/* Default tier option — hidden for TEAMS-only customers */}
                {customer.accessType !== "TEAMS" && techMeQuery.data?.tier && (
                  <option value="">
                    Use my default tier ({techMeQuery.data.tier})
                  </option>
                )}
                {/* For TEAMS-only customers without a tier, or technicians with no tier */}
                {(customer.accessType === "TEAMS" || !techMeQuery.data?.tier) && (
                  <option value="">Select activation level...</option>
                )}
                {/* Standard tier options — hidden for TEAMS-only customers */}
                {customer.accessType !== "TEAMS" && TECHNICIAN_TIERS
                  .filter((tier) => {
                    if (techMeQuery.isLoading) return false;
                    const myTier = techMeQuery.data?.tier;
                    if (!myTier) return true; // no tier assigned — show all options
                    return TIER_NUMERIC_RANK[tier] >= TIER_NUMERIC_RANK[myTier];
                  })
                  .map((tier) => {
                    const desc = TIER_ROLES[tier].description.split("—")[0]?.trim() ?? "";
                    return (
                      <option key={tier} value={tier}>
                        {tier} — {desc}
                      </option>
                    );
                  })}
                {/* Teams Specialist option — shown when customer and technician both support it */}
                {isTeamsEnabled(customer) && techMeQuery.data?.teamsSpecialist && (
                  <option value="TeamsSpecialist">
                    Teams Specialist — Teams, Exchange, User Admin
                  </option>
                )}
              </select>
              <p className="text-label text-sc-text-muted mt-1">
                {customer.accessType === "TEAMS"
                  ? "This customer uses Teams Specialist access only."
                  : "Leave blank to use your assigned tier. Select a lower tier to request fewer privileges."}
              </p>
            </div>

            {/* HaloPSA Ticket (optional) */}
            <div className="mb-6">
              <label
                htmlFor="haloTicketId"
                className="block text-body-sm font-medium text-sc-text mb-1"
              >
                HaloPSA Ticket{" "}
                <span className="text-sc-text-muted font-normal">
                  (optional)
                </span>
              </label>
              <input
                id="haloTicketId"
                type="number"
                min="1"
                value={haloTicketId}
                onChange={(e) => setHaloTicketId(e.target.value)}
                placeholder="e.g. 12345"
                className="h-9 w-full max-w-xs rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text placeholder:text-sc-text-muted [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <p className="text-label text-sc-text-muted mt-1">
                Link this session to a HaloPSA ticket to receive lifecycle comments.
              </p>
            </div>

            {/* Validation error */}
            {validationError && (
              <div className="mb-4 flex items-center gap-2 text-body-sm text-sc-danger">
                <AlertTriangle size={16} />
                {validationError}
              </div>
            )}

            {/* API error */}
            {activateSession.isError && (
              <div className="mb-4 p-3 rounded-card border border-sc-danger/30 bg-red-900/10">
                <div className="flex items-center gap-2 text-body-sm text-sc-danger">
                  <AlertTriangle size={16} />
                  {(activateSession.error as Error).message ||
                    "Failed to activate session"}
                </div>
              </div>
            )}

            {/* Activate button */}
            <Button
              onClick={handleActivate}
              disabled={
                activateSession.isPending ||
                justification.trim().length < MIN_JUSTIFICATION_LENGTH
              }
              className="w-full sm:w-auto"
              size="lg"
            >
              {activateSession.isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <ShieldCheck size={16} />
                  Activate Session
                </>
              )}
            </Button>
          </GlassCard>
        </div>

        {/* Customer info sidebar */}
        <div>
          <GlassCard>
            <h3 className="text-h3 font-display text-sc-navy mb-3">
              Customer Info
            </h3>
            <div className="space-y-0">
              <SidebarRow label="Name" value={customer.customerName} />
              <SidebarRow label="Tenant ID" value={customer.m365TenantId} mono />
              <div className="flex justify-between py-2 border-b border-sc-border/50">
                <span className="text-body-sm text-sc-text-muted">
                  Service Tier
                </span>
                <StatusBadge status={customer.serviceTier} category="tier" />
              </div>
              <div className="flex justify-between py-2 border-b border-sc-border/50">
                <span className="text-body-sm text-sc-text-muted">
                  GDAP Strategy
                </span>
                <StatusBadge
                  status={customer.accessType}
                  category="accessType"
                />
              </div>
              <SidebarRow
                label="Max Duration"
                value={`${customer.sessionDuration}h`}
              />
              <SidebarRow
                label="Approval Required"
                value={customer.pimApprovalRequired ? "Yes" : "No"}
              />
              {customer.requiresTier && (
                <SidebarRow
                  label="Min Tier"
                  value={customer.requiresTier}
                />
              )}
              {customer.requiresClearance && (
                <SidebarRow
                  label="Clearance"
                  value={customer.requiresClearance}
                />
              )}
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}

function SidebarRow({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string | number | null;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex justify-between py-2 border-b border-sc-border/50 last:border-b-0">
      <span className="text-body-sm text-sc-text-muted">{label}</span>
      <span
        className={`text-body-sm text-sc-text font-medium ${mono ? "font-mono text-label" : ""}`}
      >
        {value ?? "-"}
      </span>
    </div>
  );
}

function BackButton({
  customerId,
  navigate,
}: {
  readonly customerId: string;
  readonly navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <button
      onClick={() =>
        void navigate({
          to: "/customers/$customerId",
          params: { customerId },
        })
      }
      className="flex items-center gap-1 text-body-sm text-sc-text-muted hover:text-sc-text transition-colors"
    >
      <ArrowLeft size={16} />
      Back to Customer
    </button>
  );
}

function ActiveSessionView({
  session,
  customer,
  customerId,
  onDeactivate,
  onExtend,
  showExtend,
  setShowExtend,
  extendHours,
  setExtendHours,
  extendJustification,
  setExtendJustification,
  extendPending,
  deactivatePending,
  navigate,
}: {
  readonly session: PIMSession;
  readonly customer: { readonly customerName: string; readonly m365TenantId: string; readonly defaultDomain?: string | null };
  readonly customerId: string;
  readonly onDeactivate: (sessionId: string) => void;
  readonly onExtend: (sessionId: string) => void;
  readonly showExtend: boolean;
  readonly setShowExtend: (show: boolean) => void;
  readonly extendHours: number;
  readonly setExtendHours: (hours: number) => void;
  readonly extendJustification: string;
  readonly setExtendJustification: (text: string) => void;
  readonly extendPending: boolean;
  readonly deactivatePending: boolean;
  readonly navigate: ReturnType<typeof useNavigate>;
}) {
  const portalLinks = getPortalLinks(session, customer.defaultDomain ?? null);

  // Track client-side expiry so the banner updates when countdown reaches zero
  const [isExpired, setIsExpired] = useState(
    () => new Date(session.expiresAt).getTime() <= Date.now(),
  );

  useEffect(() => {
    const expiryMs = new Date(session.expiresAt).getTime();
    if (expiryMs <= Date.now()) {
      setIsExpired(true);
      return;
    }
    const timeout = setTimeout(() => setIsExpired(true), expiryMs - Date.now());
    return () => clearTimeout(timeout);
  }, [session.expiresAt]);

  return (
    <div className="space-y-6">
      <BackButton customerId={customerId} navigate={navigate} />

      {/* Session status header */}
      <GlassCard className={isExpired ? "border-amber-500/30 bg-amber-900/10" : "border-green-500/30 bg-green-900/10"}>
        <div className="flex items-start gap-3">
          {isExpired ? (
            <Clock size={24} className="text-amber-500 mt-0.5 shrink-0" />
          ) : (
            <CheckCircle size={24} className="text-green-500 mt-0.5 shrink-0" />
          )}
          <div className="flex-1">
            <h2 className="text-h2 font-display text-sc-navy mb-1">
              {isExpired ? "Session Expired" : "Session Active"}
            </h2>
            <p className="text-body-sm text-sc-text-muted">
              {isExpired ? (
                <>
                  Your privileged access to{" "}
                  <span className="font-medium text-sc-text">
                    {customer.customerName}
                  </span>{" "}
                  has expired
                </>
              ) : (
                <>
                  You have privileged access to{" "}
                  <span className="font-medium text-sc-text">
                    {customer.customerName}
                  </span>
                </>
              )}
            </p>
          </div>
          {!isExpired && (
            <div className="text-right">
              <span className="text-label text-sc-text-muted">Expires in</span>
              <div className="mt-1">
                <CountdownTimer expiresAt={session.expiresAt} />
              </div>
            </div>
          )}
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session details */}
        <GlassCard>
          <h3 className="text-h3 font-display text-sc-navy mb-3">
            Session Details
          </h3>
          <SidebarRow label="Customer" value={session.customerName} />
          <SidebarRow label="Group" value={session.groupName} />
          <SidebarRow label="Duration" value={`${session.durationHours}h`} />
          {session.activationTier && (
            <div className="flex justify-between py-2 border-b border-sc-border/50">
              <span className="text-body-sm text-sc-text-muted">Tier</span>
              {session.activationTier === "TeamsSpecialist" ? (
                <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                  Teams Specialist
                </span>
              ) : (
                <StatusBadge
                  status={session.activationTier}
                  category="techTier"
                />
              )}
            </div>
          )}
          <div className="flex justify-between py-2 border-b border-sc-border/50">
            <span className="text-body-sm text-sc-text-muted">Status</span>
            <StatusBadge status={session.status} category="pim" />
          </div>
          <SidebarRow
            label="Activated"
            value={new Date(session.activatedAt).toLocaleString()}
          />
          <SidebarRow
            label="Expires"
            value={new Date(session.expiresAt).toLocaleString()}
          />
          <div className="mt-3 pt-3 border-t border-sc-border">
            <span className="text-label text-sc-text-muted uppercase tracking-widest">
              Justification
            </span>
            <p className="text-body-sm text-sc-text mt-1">
              {session.justification}
            </p>
          </div>

          {/* Roles */}
          {session.roles.length > 0 && (
            <div className="mt-3 pt-3 border-t border-sc-border">
              <span className="text-label text-sc-text-muted uppercase tracking-widest">
                Roles ({session.roles.length})
              </span>
              <div className="flex flex-wrap gap-1 mt-2">
                {session.roles.map((roleId) => {
                  const displayName = ENTRA_ROLE_DISPLAY_NAMES[roleId as keyof typeof ENTRA_ROLE_DISPLAY_NAMES] ?? roleId;
                  return (
                    <span
                      key={roleId}
                      className="inline-block px-2 py-0.5 text-badge rounded-full bg-sc-primary/10 text-sc-primary"
                      title={roleId}
                    >
                      {displayName}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </GlassCard>

        {/* Actions + Portal links */}
        <div className="space-y-6">
          {/* Admin portal links (hidden when expired) */}
          {!isExpired && portalLinks.length > 0 && (
            <GlassCard>
              <h3 className="text-h3 font-display text-sc-navy mb-3">
                Admin Portals
              </h3>
              <p className="text-body-sm text-sc-text-muted mb-3">
                Access customer admin portals with your elevated privileges.
              </p>
              <div className="space-y-1">
                {portalLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-btn text-body-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  >
                    <ExternalLink size={14} />
                    {link.name}
                  </a>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Session actions (hidden when expired) */}
          {!isExpired && <GlassCard>
            <h3 className="text-h3 font-display text-sc-navy mb-3">
              Actions
            </h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setShowExtend(!showExtend)}
              >
                <Clock size={16} />
                Extend Session
              </Button>

              {showExtend && (
                <div className="p-3 rounded-card border border-sc-border bg-sc-surface-hover/30 space-y-3">
                  <div>
                    <label className="block text-body-sm font-medium text-sc-text mb-1">
                      Additional Hours
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 4].map((h) => (
                        <button
                          key={h}
                          onClick={() => setExtendHours(h)}
                          className={`px-3 py-1.5 rounded-btn text-body-sm font-medium transition-colors ${
                            extendHours === h
                              ? "bg-sc-primary text-white"
                              : "text-sc-text-muted border border-sc-border hover:bg-sc-surface-hover"
                          }`}
                        >
                          +{h}h
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="extend-justification"
                      className="block text-body-sm font-medium text-sc-text mb-1"
                    >
                      Reason for Extension
                    </label>
                    <textarea
                      id="extend-justification"
                      value={extendJustification}
                      onChange={(e) => setExtendJustification(e.target.value)}
                      placeholder="Why do you need more time?"
                      rows={2}
                      className="w-full rounded-input border border-sc-border bg-sc-input-bg px-3 py-2 text-body-sm text-sc-text placeholder:text-sc-text-muted resize-none focus:outline-none focus:ring-2 focus:ring-sc-primary/50"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onExtend(session.id)}
                    disabled={
                      extendPending ||
                      extendJustification.trim().length < MIN_JUSTIFICATION_LENGTH
                    }
                  >
                    {extendPending ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Extending...
                      </>
                    ) : (
                      `Extend by ${extendHours}h`
                    )}
                  </Button>
                </div>
              )}

              <Button
                variant="outline"
                className="w-full justify-start text-sc-danger hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => onDeactivate(session.id)}
                disabled={deactivatePending}
              >
                {deactivatePending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ShieldCheck size={16} />
                )}
                {deactivatePending ? "Deactivating..." : "Deactivate Session"}
              </Button>
            </div>
          </GlassCard>}
        </div>
      </div>
    </div>
  );
}

/** Role-to-portal mapping: which Entra roles grant access to which admin portals */
const ROLE_PORTAL_MAP: Record<string, readonly string[]> = {
  // User Admin
  "fe930be7-5e62-47db-91af-98c3a49a38b1": [
    "Microsoft 365 Admin Center",
    "Microsoft Entra Admin Center",
  ],
  // Teams Admin
  "69091246-20e8-4a56-aa4d-066075b2a7a8": [
    "Microsoft Teams Admin Center",
  ],
  // Exchange Admin
  "29232cdf-9323-42fd-ade2-1d097af3e4de": [
    "Exchange Admin Center",
  ],
  // SharePoint Admin
  "f28a1f50-f6e7-4571-818b-6a12f2af6b6c": [
    "SharePoint Admin Center",
  ],
  // Intune Admin
  "3a2c62db-5318-420d-8d74-23affee5d9d5": [
    "Microsoft Intune (Endpoint Manager)",
  ],
  // Security Admin
  "194ae4cb-b126-40b2-bd5b-6091b380977d": [
    "Microsoft Defender Portal",
    "Microsoft Security Copilot",
    "Defender for Cloud Apps",
  ],
  // Compliance Admin
  "ac434307-12b9-4fa1-a708-88bf58caabc1": [
    "Microsoft Purview (Compliance)",
  ],
  // Global Admin
  "62e90394-69f5-4237-9190-012177145e10": [
    "Azure Portal",
  ],
  // Power Platform Admin
  "11648597-926c-4cf3-9c36-bcebb0ba8dcc": [
    "Power Platform Admin Center",
  ],
  // Fabric/Power BI Admin
  "a9ea8996-122f-4c74-9520-8edcd192826c": [
    "Power BI",
  ],
  // Dynamics 365 Admin
  "44367163-eba1-44c3-98af-f5787879f96a": [
    "Dynamics 365 Business Central",
    "Dynamics 365 Lifecycle Services",
  ],
  // Global Reader
  "f2ef992c-3afb-46b9-b7cf-a126ee74c451": [
    "Microsoft 365 Admin Center",
  ],
};

function getPortalLinks(
  session: PIMSession,
  customerDomain: string | null,
): { name: string; url: string }[] {
  if (session.status !== "Active") return [];

  const activationTier = session.activationTier;

  // For TeamsSpecialist or any role-based activation, filter portals by session roles
  if (activationTier === "TeamsSpecialist" || !activationTier) {
    const sessionRoles = session.roles ?? [];
    const relevantPortalNames = new Set<string>();
    for (const roleId of sessionRoles) {
      const portals = ROLE_PORTAL_MAP[roleId];
      if (portals) portals.forEach((p) => relevantPortalNames.add(p));
    }
    return ADMIN_PORTAL_LINKS
      .filter((portal) => relevantPortalNames.has(portal.name))
      .filter((portal) => !portal.requiresDomain || customerDomain)
      .map((portal) => ({
        name: portal.name,
        url: portal.urlTemplate
          .replace("{tenantId}", session.customerTenantId)
          .replace("{customerDomain}", customerDomain ?? "")
          .replace("{realm}", MSP_REALM),
      }));
  }

  // Standard tier-based filtering
  const tier = activationTier as TechnicianTier;
  return ADMIN_PORTAL_LINKS
    .filter((portal) => portal.relevantTiers.includes(tier))
    .filter((portal) => !portal.requiresDomain || customerDomain)
    .map((portal) => ({
      name: portal.name,
      url: portal.urlTemplate
        .replace("{tenantId}", session.customerTenantId)
        .replace("{customerDomain}", customerDomain ?? "")
        .replace("{realm}", MSP_REALM),
    }));
}
